import React from "react";
import config from './config'
import authService from "./AuthService";
import Paho from "paho-mqtt"
import "./LiveEndpointView.css"
import toast from "react-hot-toast";
import OverlayChart from "./OverlayChart";
import {ReactComponent as InfluxLogo} from "./influx.svg";
import {ApplianceStatus} from "./Endpoint";

export type ApplianceValues = Map<string, UpdateEvent>

type UpdateEvent = {
    time: number,
    rate: number,
    dataPoints: object
}

type DataPoint = {
    // Mandatory fields of a data point
    type: string,
    value: any,

    // Optional fields of a data point if of type "BinaryReading", "NumericReading" or "StringReading"
    quality?: string,

    // Optional fields of a data point if of type "NumericReading"
    unit?: string,

    // Optional fields of a data point if of type "Error"
    severity?: string,
    message?: string,

    // Optional fields of a data point if of type "Enum"
    enumType?: string,
    stringValue?: string,
    validValues?: Array<string>,

    // Optional fields of a data point if of type "Bitmap"
    bitmapType?: string,
    bits?: any,

    // Optional fields of a data point if of type "Enum" or "Bitmap"
    writable?: boolean
}

type EditedDataPoint = {
    applianceId: string,
    serviceUri: string,
    dataPointName: string,
    dataPoint: DataPoint,
    newValue: any
}

type EditedUpdateRate = {
    applianceId: string,
    serviceUri: string,
    newValue: number
}

type GraphDataPoint = {
    applianceId: string,
    serviceUri: string,
    dataPointName: string,
    unit: string,
}

interface PendingOperation {
    handleMqttMessage(message: Paho.Message): boolean;
}

class PendingOperateOperation implements PendingOperation {
    private readonly endpointId: string;
    private readonly applianceId: string;
    private readonly serviceUri: string;
    private readonly dataPointName: string;
    private readonly dataPointType: string;
    private readonly expectedValue: any;
    private resolve?: () => void;
    private reject?: () => void;

    constructor(endpointId: string, applianceId: string, serviceUri: string, dataPointName: string, dataPointType: string, expectedValue: any, timeout: number = 5000) {
        this.endpointId = endpointId;
        this.applianceId = applianceId;
        this.serviceUri = serviceUri;
        this.dataPointName = dataPointName;
        this.dataPointType = dataPointType;
        this.expectedValue = expectedValue;

        toast.promise(new Promise<void>((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
            setTimeout(reject, timeout);
        }), {
            loading: <span>Operating <b>{dataPointName}</b>...</span>,
            success: <span>Successfully operated <b>{dataPointName}</b>.</span>,
            error: <span>Failed to operate <b>{dataPointName}</b>!</span>
        });
    }

    handleMqttMessage(message: Paho.Message): boolean {
        if (message.destinationName != this.endpointId + "/" + this.applianceId + "/" + this.serviceUri + "/@UPDATE") return false;
        if (message.payloadString === "") return false;
        const updateEvent = JSON.parse(message.payloadString) as UpdateEvent;
        if (!updateEvent) {
            if (this.reject) this.reject();
            return true;
        }
        const dataPointEntry = Object.entries(updateEvent.dataPoints).find((entry) => entry[0] == this.dataPointName);
        if (!dataPointEntry) {
            if (this.reject) this.reject();
            return true;
        }
        const dataPoint = dataPointEntry[1];
        if (dataPoint.type !== this.dataPointType) {
            if (this.reject) this.reject();
            return true;
        }

        Object.keys(this.expectedValue).forEach((key) => {
            if (this.expectedValue[key] !== dataPoint[key]) {
                if (this.reject) this.reject();
                return true;
            }
        });

        if (this.resolve) this.resolve();
        return true;

    }
}

type LiveEndpointViewProps = {
    endpointId: string
    onlineStateChangeCallback?: (online: boolean) => void
    statusReceivedCallback?: (status: string) => void
    eventsReceivedCallback?: (event: string) => void
    initialAppliances?: object
    applianceStatuses?: Array<ApplianceStatus>
    allowedApplianceIds?: Array<string>
}

type LiveEndpointViewState = {
    isOnline: boolean,
    appliances: Map<string, ApplianceValues>
    collapsed: Map<string, boolean>
    editedDataPoint?: EditedDataPoint
    editedUpdateRate?: EditedUpdateRate
    graphDataPoint?: GraphDataPoint
}

class LiveEndpointView extends React.Component<LiveEndpointViewProps, LiveEndpointViewState> {
    private client: Paho.Client
    private pendingOperations: Array<PendingOperation> = [];

    constructor(props: LiveEndpointViewProps) {
        super(props);

        let appliances = new Map<string, ApplianceValues>();
        let collapsed = new Map<string, boolean>();
        if (props.initialAppliances) {
            Object.entries(props.initialAppliances).forEach((entry) => {
                let values  = new Map<string, UpdateEvent>();
                Object.entries(entry[1]["services"] as any).forEach((entry) => {
                    values.set(entry[0], entry[1] as UpdateEvent);
                });
                appliances.set(entry[0], values);
                collapsed.set(entry[0], true);
            });
        }

        this.state = {
            isOnline: true,
            appliances: appliances,
            collapsed: collapsed,
            editedDataPoint: undefined,
            editedUpdateRate: undefined,
            graphDataPoint: undefined,
        };
        this.client = new Paho.Client(config.MqttUri, "web" + (Math.random() * 100));
    }

    componentDidMount() {
        this.client.onMessageArrived = this.onMqttMessage;
        this.client.onConnectionLost = this.onMqttConnectionLost;
        this.getTokenAndConnect();
    }

    componentWillUnmount() {
        if (this.client.isConnected()) {
            this.client.disconnect();
        }
    }

    private toggleCollapsed = (applianceId: string) => {
        let collapsed = this.state.collapsed;
        collapsed.set(applianceId, !collapsed.get(applianceId));
        this.setState({
            collapsed: collapsed
        });
    }

    render() {
        // Appliances with live data, then the appliances of the status without data services (e.g. I/O appliances), so their state is visible too.
        const applianceIds = [...this.state.appliances.keys()];
        this.props.applianceStatuses?.forEach((status) => {
            if (!applianceIds.includes(status.id)) applianceIds.push(status.id);
        });

        const allowedIds = this.props.allowedApplianceIds;
        const visibleApplianceIds = allowedIds
            ? applianceIds.filter(id => allowedIds.includes(id))
            : applianceIds;

        return <div className="appliance-live-data">
            {visibleApplianceIds.length !== 0 ? visibleApplianceIds.map((applianceId) => {
                return this.renderAppliance(applianceId, this.state.appliances.get(applianceId) ?? new Map<string, UpdateEvent>());
            }) : <div className="noRealtimeData">No live data</div>}
            {this.state.editedDataPoint && this.renderDataPointEditor()}
            {this.state.graphDataPoint && <OverlayChart endpointId={this.props.endpointId}
                                                        applianceId={this.state.graphDataPoint.applianceId}
                                                        serviceUri={this.state.graphDataPoint.serviceUri}
                                                        dataPoint={this.state.graphDataPoint.dataPointName}
                                                        unit={this.state.graphDataPoint.unit}
                                                        closeCallback={() => this.setState({
                                                            graphDataPoint: undefined
                                                        })}/>}
        </div>;
    }

    private renderAppliance(applianceId: string, appliance: ApplianceValues) {
        let name = applianceId;
        let state = "UNKNOWN";
        const applianceStatus = this.findApplianceStatus(applianceId);
        if (applianceStatus !== undefined) {
            state = applianceStatus.state;
            name = applianceStatus.friendlyName || applianceId;
        }

        return <div className="appliance" key={applianceId}>
            <h4 className={state} onClick={() => this.toggleCollapsed(applianceId)}>{name}
                {(state !== "RUNNING") && <small className="appliance-state">({state})</small>}
            </h4>
            <div className={"appliance-data" + (this.state.collapsed.get(applianceId) === true ? " collapsed" : "")}>
            {this.renderDeviceInformation(applianceStatus?.deviceInformation)}
            {appliance.size !== 0 ? [...appliance.keys()].map((serviceUri) => {
                return this.renderUpdateEvent(applianceId, serviceUri, appliance.get(serviceUri)!)
            }) : <div className="noData">No data services</div>}
            </div>
        </div>;
    }

    private renderDeviceInformation(deviceInformation?: Record<string, string>) {
        const entries = Object.entries(deviceInformation ?? {}).filter((entry) => entry[1] !== "");
        if (entries.length === 0) return null;

        const labels: Record<string, string> = {
            manufacturer: "Manufacturer",
            model: "Model",
            serialNumber: "Serial number",
            hardwareRevision: "Hardware revision",
            softwareRevision: "Software revision",
        };
        return <div className="service device-information">
            <h5>Device information</h5>
            <div className="dataPointList">
                {entries.map((entry) => {
                    return <div className="dataPoint" key={entry[0]}>
                        <span className="name">{labels[entry[0]] ?? entry[0]}</span>
                        <span className="value">{entry[1]}</span>
                    </div>;
                })}
            </div>
        </div>;
    }

    private renderUpdateEvent(applianceId: string, serviceUri: string, event: UpdateEvent) {
        return <div className="service" key={applianceId + "/" + serviceUri}>
            <h5>{serviceUri}</h5>
            <div className="time-and-rate">
                <span className="label">Last update:</span>
                <span
                    className={event.time * 1000 < (Date.now() - (event.rate + 5 * 60) * 1000) ? "value bad" : "value"}>{(new Date(event.time * 1000)).toLocaleString()}</span>
                <span className="label">Update rate:</span>
                <span className="value">
                {event.rate == 0 ? "Push" : (event.rate + "s")}
                </span>
            </div>
            {event.dataPoints && Object.entries(event.dataPoints).length > 0 ?
                <div className="dataPointList">
                    {Object.entries(event.dataPoints).map((dataPoint) => {
                        return <div className="dataPoint" key={applianceId + "/" + serviceUri + "/" + dataPoint[0]}>
                            <span className="name">{dataPoint[0]}</span>
                            {this.renderDataPointType(dataPoint[1])}
                            {this.renderDataPointValue(applianceId, serviceUri, dataPoint[0], dataPoint[1])}
                            <button className="LiveEndpointView-graph" onClick={() =>
                                this.setState({
                                    graphDataPoint: {
                                        applianceId: applianceId,
                                        serviceUri: serviceUri,
                                        dataPointName: dataPoint[0],
                                        unit: dataPoint[1].unit || "",
                                    }
                                })
                            }><InfluxLogo /></button>
                        </div>;
                    })}
                </div> : <div className="noData">Empty Dataset</div>}
        </div>;
    }

    private renderDataPointType(dataPoint: DataPoint) {
        switch (dataPoint.type) {
            case "Enum":
                if (dataPoint.enumType) {
                    return <span className="type">{dataPoint.enumType} <br/> (Enum {dataPoint.writable ? "r/w" : "ro"}) </span>;
                } else {
                    return <span className="type">Enum {dataPoint.writable ? "(r/w)" : "(ro)"}</span>;
                }
            case "Bitmap":
                if (dataPoint.bitmapType) {
                    return <span className="type">{dataPoint.bitmapType} <br/> (Bitmap {dataPoint.writable ? "r/w" : "ro"}) </span>;
                } else {
                    return <span className="type">Bitmap {dataPoint.writable ? " (r/w)" : "(ro)"} </span>;
                }
            default:
                return <span className="type">{dataPoint.type}</span>
        }
    }

    private renderDataPointValue(applianceId: string, serviceUri: string, dataPointName: string, dataPoint: DataPoint) {
        switch (dataPoint.type) {
            case "BinaryReading":
                return <span className={"value binary" + (dataPoint.value ? " active" : "")}>{dataPoint.value ? "On" : "Off"}</span>

            case "BinarySetPoint":
                return <span className={"value binary writable" + (dataPoint.value ? " active" : "")}
                             onClick={() => this.toggleBinarySetPoint(applianceId, serviceUri, dataPointName, dataPoint)}>{dataPoint.value ? "On" : "Off"}</span>

            case "NumericReading":
                return <span className="value">{dataPoint.value.toFixed(2)} <span className="unit"> {this.renderUnit(dataPoint.unit || "")} </span></span>

            case "NumericSetPoint":
                return <span className="value writable"
                             onClick={() => this.setState({
                                 editedDataPoint: {
                                     applianceId: applianceId,
                                     serviceUri: serviceUri,
                                     dataPointName: dataPointName,
                                     dataPoint: dataPoint,
                                     newValue: dataPoint.value
                                 }
                             })}>{dataPoint.value.toFixed(2)} <span className="unit"> {this.renderUnit(dataPoint.unit || "")} </span></span>

            case "StringSetPoint":                 
                const stringSetPointValue = dataPoint.stringValue ?? dataPoint.value ?? "";
                return <span className="value writable" onClick={() => this.setState({
                    editedDataPoint: {
                        applianceId: applianceId,
                        serviceUri: serviceUri,
                        dataPointName: dataPointName,
                        dataPoint: dataPoint,
                        newValue: stringSetPointValue
                    }
                })}>{stringSetPointValue}</span>

            case "Enum":
                let enumValue = dataPoint.value;
                if (dataPoint.stringValue) {
                    enumValue = dataPoint.stringValue;
                }
                if (dataPoint.writable) {
                    return <span className="value writable" onClick={() => this.setState({
                        editedDataPoint: {
                            applianceId: applianceId,
                            serviceUri: serviceUri,
                            dataPointName: dataPointName,
                            dataPoint: dataPoint,
                            newValue: dataPoint.stringValue
                        }
                    })}>{enumValue}</span>
                } else {
                    return <span className="value">{enumValue}</span>
                }

            case "Bitmap":
                let bitmapValue = dataPoint.value;
                if (dataPoint.writable) {
                    if (dataPoint.bits) {
                        bitmapValue = Object.entries(dataPoint.bits).map((entry) =>
                            <div onClick={() => this.toggleBitmapBit(applianceId, serviceUri, dataPointName, dataPoint, entry[0])}
                                 className={entry[1] ? "writable active" : "writable"}>{entry[0]}</div>);
                    }
                    return <span className="value bitmap">{bitmapValue}</span>
                } else {
                    if (dataPoint.bits) {
                        bitmapValue = Object.entries(dataPoint.bits).map((entry) =>
                            <div className={entry[1] ? "active" : ""}>{entry[0]}</div>);
                    }
                    return <span className="value bitmap">{bitmapValue}</span>
                }

            default:
                return <span className="value">{dataPoint.value}</span>
        }
    }

    private renderUnit(unit: string): string {
        switch (unit) {
            case "NONE":
                return "[ ]";

            case "VOLTS":
                return "[V]";

            case "AMPERES":
                return "[A]";

            case "WATTS":
                return "[W]";

            case "JOULES":
                return "[J]";

            case "OHMS":
                return "[]";

            case "KELVIN":
                return "[K]";

            case "PERCENT":
                return "[%]";

            case "HERTZ":
                return "[Hz]";

            case "VOLT_AMPERES":
                return "[VA]";

            case "VOLT_AMPERES_REACTIVE":
                return "[VAr]";

            case "RPM":
                return "[rpm]";

            default:
                return unit;
        }
    }

    private renderDataPointEditor() {
        switch (this.state.editedDataPoint?.dataPoint.type) {
            case "NumericSetPoint":
                return <div className="LiveEndpointView-editor-overlay">
                    <div className="LiveEndpointView-editor">
                        <span className="LiveEndpointView-editorTitle">{this.state.editedDataPoint!.dataPointName}</span>
                        <input type="number" step={0.01} value={this.state.editedDataPoint!.newValue} onChange={(event) => this.setState({
                            editedDataPoint: {
                                ...this.state.editedDataPoint!,
                                newValue: parseFloat(event.target.value)
                            }
                        })}/>
                        <div className="LiveEndpointView-buttonBox">
                            <button onClick={this.setNumericDataPoint}>Ok</button>
                            <button onClick={() => this.setState({
                                editedDataPoint: undefined
                            })}>Cancel
                            </button>
                        </div>
                    </div>
                </div>;

            case "StringSetPoint":
                return <div className="LiveEndpointView-editor-overlay">
                    <div className="LiveEndpointView-editor">
                        <span className="LiveEndpointView-editorTitle">{this.state.editedDataPoint!.dataPointName}</span>
                        <input type="text" value={this.state.editedDataPoint!.newValue} onChange={(event) => this.setState({
                            editedDataPoint: {
                                ...this.state.editedDataPoint!,
                                newValue: event.target.value
                            }
                        })}/>
                        <div className="LiveEndpointView-buttonBox">
                            <button onClick={this.setStringDataPoint}>Ok</button>
                            <button onClick={() => this.setState({
                                editedDataPoint: undefined
                            })}>Cancel
                            </button>
                        </div>
                    </div>
                </div>;

            case "Enum":
                return <div className="LiveEndpointView-editor-overlay">
                    <div className="LiveEndpointView-editor">
                        <span className="LiveEndpointView-editorTitle">{this.state.editedDataPoint!.dataPointName}</span>
                        <select value={this.state.editedDataPoint!.newValue} onChange={(event) => this.setState({
                            editedDataPoint: {
                                ...this.state.editedDataPoint!,
                                newValue: event.target.value
                            }
                        })}>
                            {this.state.editedDataPoint!.dataPoint.validValues!.map((value) => {
                                return <option value={value}>{value}</option>
                            })}
                        </select>
                        <div className="LiveEndpointView-buttonBox">
                            <button onClick={this.setEnumDataPoint}>Ok</button>
                            <button onClick={() => this.setState({
                                editedDataPoint: undefined
                            })}>Cancel
                            </button>
                        </div>
                    </div>
                </div>;

            default:
                return null;
        }
    }

    private async getTokenAndConnect() {
        try {
            const mqttOptions = await authService.getMqttOptions(this.props.endpointId);

            let useSSL = false;
            try {
                useSSL = new URL(config.MqttUri).protocol === "wss:";
            } catch {
                useSSL = config.MqttUri.startsWith("wss://");
            }
        
            const connectOptions = {
                ...mqttOptions,
                useSSL,
                onSuccess: this.onMqttConnected,
                onFailure: (err: Paho.ErrorWithInvocationContext) => {
                    console.error("Failed to connect to MQTT broker:", err.errorMessage);
                }
            };
        
        this.client.connect(connectOptions);
        } catch (e) {
            console.error("Failed to connect to MQTT broker: " + e);
        }
    }

    private onMqttConnected = () => {
        console.log("Connected to MQTT broker");
        this.client.subscribe(this.props.endpointId + "/+/+/@UPDATE")
        this.client.subscribe(this.props.endpointId + "/+/+/@RATE")
        this.client.subscribe(this.props.endpointId + "/@STATUS")

        if (this.props.onlineStateChangeCallback) {
            this.client.subscribe(this.props.endpointId + "/@ONLINE")
        }

        if (this.props.eventsReceivedCallback) {
            this.client.subscribe(this.props.endpointId + "/@EVENTS")
        }
    }

    private onMqttMessage = (message: Paho.Message) => {
        const comp = message.destinationName.split("/");

        this.pendingOperations = this.pendingOperations.filter((operation) => {
            return !operation.handleMqttMessage(message);
        });

        switch (comp[comp.length - 1]) {
            case "@UPDATE": {
                if (comp.length != 4) return;
                const applianceName = comp[1];
                const serviceUri = comp[2];
                if (message.payloadString === "") {
                    let appliances = this.state.appliances;
                    if (appliances.has(applianceName)) {
                        appliances.get(applianceName)!.delete(serviceUri);
                        if (appliances.get(applianceName)!.size == 0) {
                            appliances.delete(applianceName);
                        }
                        this.setState({
                            appliances: appliances
                        });
                    }
                    return;
                }
                ;
                const updateEvent = JSON.parse(message.payloadString) as UpdateEvent;
                if (!updateEvent) return;

                let appliances = this.state.appliances;
                if (!appliances.has(applianceName)) {
                    appliances.set(applianceName, new Map<string, UpdateEvent>());
                }
                appliances.get(applianceName)!.set(serviceUri, updateEvent)
                this.setState({
                    appliances: appliances
                });
                break;
            }

            case "@RATE": {
                if (comp.length != 4) return;
                if (message.payloadString === "") return;
                const applianceName = comp[1];
                const serviceUri = comp[2];
                const rate = parseInt(message.payloadString);

                let appliances = this.state.appliances;
                if (!appliances.has(applianceName)) return;
                if (!appliances.get(applianceName)!.has(serviceUri)) return;
                appliances.get(applianceName)!.get(serviceUri)!.rate = rate;
                this.setState({
                    appliances: appliances
                });
                break;
            }

            case "@ONLINE": {
                if (this.props.onlineStateChangeCallback) {
                    this.props.onlineStateChangeCallback(message.payloadString == "1")
                }
                break;
            }

            case "@STATUS": {
                if (this.props.statusReceivedCallback) {
                    this.props.statusReceivedCallback(message.payloadString);
                }
                break;
                }

            case "@EVENTS": {
                    if (this.props.eventsReceivedCallback) {
                        this.props.eventsReceivedCallback(message.payloadString);
                    }
                    break;
            }
        }
    }

    private onMqttConnectionLost = () => {
        this.getTokenAndConnect();
    }

    private toggleBinarySetPoint = (applianceId: string, serviceUri: string, dataPointName: string, dataPoint: DataPoint) => {
        let message = new Paho.Message(JSON.stringify([
            {
                name: dataPointName,
                type: "BinarySetPoint",
                value: !dataPoint.value
            }
        ]));

        this.pendingOperations.push(new PendingOperateOperation(this.props.endpointId, applianceId, serviceUri, dataPointName, dataPoint.type, {
            value: !dataPoint.value
        }));

        message.destinationName = this.props.endpointId + "/" + applianceId + "/" + serviceUri + "/@OPERATE";
        message.qos = 0;
        message.retained = false;

        this.client.send(message);
    }

    private toggleBitmapBit = (applianceId: string, serviceUri: string, dataPointName: string, dataPoint: DataPoint, bit: string) => {
        if (!dataPoint.bits) return;
        const value: any = {}
        value[bit] = !dataPoint.bits[bit];

        this.pendingOperations.push(new PendingOperateOperation(this.props.endpointId, applianceId, serviceUri, dataPointName, dataPoint.type, { /* TODO: Not so simple to control */}));

        let message = new Paho.Message(JSON.stringify([{
            name: dataPointName,
            type: "Bitmap",
            value: value
        }]));
        message.destinationName = this.props.endpointId + "/" + applianceId + "/" + serviceUri + "/@OPERATE";
        message.qos = 0;
        message.retained = false;

        this.client.send(message);
    }

    private setNumericDataPoint = () => {
        this.pendingOperations.push(new PendingOperateOperation(this.props.endpointId, this.state.editedDataPoint!.applianceId, this.state.editedDataPoint!.serviceUri, this.state.editedDataPoint!.dataPointName, this.state.editedDataPoint!.dataPoint.type, {
            value: this.state.editedDataPoint!.newValue
        }));

        let message = new Paho.Message(JSON.stringify([{
            name: this.state.editedDataPoint!.dataPointName,
            type: "NumericSetPoint",
            value: this.state.editedDataPoint!.newValue
        }]));
        message.destinationName = this.props.endpointId + "/" + this.state.editedDataPoint!.applianceId + "/" + this.state.editedDataPoint!.serviceUri + "/@OPERATE";
        message.qos = 0;
        message.retained = false;

        this.client.send(message);
        this.setState({
            editedDataPoint: undefined
        });
    }

    private setStringDataPoint = () => {
        this.pendingOperations.push(new PendingOperateOperation(this.props.endpointId, this.state.editedDataPoint!.applianceId, this.state.editedDataPoint!.serviceUri, this.state.editedDataPoint!.dataPointName, this.state.editedDataPoint!.dataPoint.type, {
            value: this.state.editedDataPoint!.newValue
        }));
        let message = new Paho.Message(JSON.stringify([{
            name: this.state.editedDataPoint!.dataPointName,
            type: "StringSetPoint",
            value: this.state.editedDataPoint!.newValue
        }]));
        message.destinationName = this.props.endpointId + "/" + this.state.editedDataPoint!.applianceId + "/" + this.state.editedDataPoint!.serviceUri + "/@OPERATE";
        message.qos = 0;
        message.retained = false;

        this.client.send(message);
        this.setState({
            editedDataPoint: undefined
        });
    }

    private setEnumDataPoint = () => {
        this.pendingOperations.push(new PendingOperateOperation(this.props.endpointId, this.state.editedDataPoint!.applianceId, this.state.editedDataPoint!.serviceUri, this.state.editedDataPoint!.dataPointName, this.state.editedDataPoint!.dataPoint.type, {
            stringValue: this.state.editedDataPoint!.newValue
        }));

        let message = new Paho.Message(JSON.stringify([{
            name: this.state.editedDataPoint!.dataPointName,
            type: "Enum",
            value: this.state.editedDataPoint!.newValue
        }]));
        message.destinationName = this.props.endpointId + "/" + this.state.editedDataPoint!.applianceId + "/" + this.state.editedDataPoint!.serviceUri + "/@OPERATE";
        message.qos = 0;
        message.retained = false;

        this.client.send(message);
        this.setState({
            editedDataPoint: undefined
        });
    }

    private findApplianceStatus(applianceId: string): ApplianceStatus | undefined {
        return this.props.applianceStatuses?.find((status) => status.id === applianceId);
    }
}

export default LiveEndpointView;